import { Device, DeviceEntryProps, DeviceType, ProfileType } from '@ixicare/resources';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@ixicare/ui';
import SiteStore from '~modules/sites/store/Site';
import { useDeviceDetail } from '~modules/sites/hooks/useDeviceDetail';
import { mapDBGWithType } from '~modules/devices/utils/deviceMapping';
import { useDeviceArchive } from '~modules/sites/hooks/useDeviceArchive';
import { useStores } from '~store/utils';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import {
  navigateMapAction,
  editAction,
  moveAction,
  deleteAction,
  deleteDeviceDataAction,
  configAction,
  ConfigurableDevice,
  updateFirmwareAction,
} from '../deviceTableActions';
import { GenericTable } from '../GenericTable';
import { TransformContext } from '../rowTransformers';
import { BaseTableConfig, WithColumnManager, WithExport, WithTransformer } from '../types';
import { useSortableQuery } from '../hooks/useSortableQuery';
import { useTableRowActions } from '../hooks/useTableRowActions';
import { useOnReachBottom } from '../utils';
import { UpdateDeviceModal } from '~modules/devices/components/DeviceModals/UpdateDeviceModal';
import { DeleteDeviceModal } from '~modules/devices/components/DeviceModals/DeleteDeviceModal';
import { DeleteDeviceDataModal } from '~modules/devices/components/DeviceModals/DeleteDeviceDataModal';
import { useDecommissionActions } from '~modules/devices/hooks/useDecommissionActions';
import { PENDING_STATUSES, useOpenOtaTasksByDevice } from '~modules/firmware/hooks/useOtaTasks';
import { UpdateFirmwareModal } from '~modules/firmware/components/UpdateFirmwareModal';

interface DevicesTableProps {
  site: SiteStore | null;
  isArchived: boolean;
  config: BaseTableConfig &
    WithTransformer<DeviceEntryProps> &
    WithColumnManager<DeviceEntryProps> &
    WithExport<DeviceEntryProps>;
  deviceTypeOverride?: DeviceType[];
  toolbarActions?: React.ReactNode;
  selectedKeys?: Set<string>;
  onSelectedKeysChange?: (next: Set<string>) => void;
}

export function DevicesTable(props: DevicesTableProps) {
  const { site, isArchived, config, deviceTypeOverride } = props;

  const deviceTypes = (deviceTypeOverride ?? config.deviceTypes)?.join(',');
  const filterKey = `${site?.uuid ?? ''}|${isArchived}|${deviceTypes}`;

  return <DevicesTableInner key={filterKey} {...props} />;
}

function DevicesTableInner({
  site,
  isArchived,
  config,
  deviceTypeOverride,
  toolbarActions,
  selectedKeys,
  onSelectedKeysChange,
}: DevicesTableProps) {
  const { notifications, device, auth, permissions } = useStores();
  const navigate = useNavigate();

  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;
  const [dataDevices, setDataDevices] = React.useState<DBGWithType[]>([]);

  const deviceTypeParam = deviceTypeOverride?.join(',') ?? config.deviceTypes?.join(',');

  const queryFn = ({ page, perPage, orderBy, order }) =>
    Device.getDevices({
      site: site?.uuid,
      page,
      perPage,
      orderBy,
      order,
      deviceType: deviceTypeParam,
      isArchived,
    });

  const {
    sortedData,
    orderBy,
    ascOrder,
    mode,
    handleSort,
    refetch,
    isRefreshing,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    loadAll,
  } = useSortableQuery<DeviceEntryProps>({
    queryKey: config.tableId,
    queryFn,
    scope: [site?.uuid, isArchived, deviceTypeOverride],
    enabled: !!site?.uuid,
    defaultOrderBy: config.defaultOrderBy ?? 'id',
    defaultAscOrder: config.defaultAscOrder,
    sortAccessors: config.sortAccessors,
  });

  const canUpdateFirmware = permissions.has('can_manage_firmware') && !isArchived;
  const otaTaskByDeviceId = useOpenOtaTasksByDevice(site?.uuid, canUpdateFirmware);
  const [firmwareDevices, setFirmwareDevices] = React.useState<DeviceEntryProps[]>([]);
  const plannedDeviceIds = React.useMemo(
    () =>
      new Set(
        [...otaTaskByDeviceId.entries()]
          .filter(([, task]) => PENDING_STATUSES.includes(task.status))
          .map(([deviceId]) => deviceId)
      ),
    [otaTaskByDeviceId]
  );

  const context: TransformContext = {
    isArchived,
    siteId: site?.id,
    otaTaskByDeviceId,
    onOpenFirmware: canUpdateFirmware ? (dev: DeviceEntryProps) => setFirmwareDevices([dev]) : undefined,
  };

  const rows = React.useMemo(() => {
    return sortedData.map((x) => config.transformRow(x, context));
  }, [sortedData, isArchived, site?.id, otaTaskByDeviceId, canUpdateFirmware]);

  useOnReachBottom(fetchNextPage, !!site?.uuid && mode === 'paginated' && !isRefreshing);

  const devicesWithType = React.useMemo(() => {
    return mapDBGWithType(sortedData);
  }, [sortedData]);

  const { selectedDevices, setSelectedDevices, selectedDeviceDetail, handleCloseDetail } =
    useDeviceDetail(devicesWithType);

  const { devicesToArchive, setDevicesToArchive, deleteMutation } = useDeviceArchive({
    onRefresh: refetch,
    notifications,
  });

  const { actions: decommissionActions, modals: decommissionModals } = useDecommissionActions({
    isEmployee,
    onRefresh: refetch,
  });

  const onConfigAction = (devices: ConfigurableDevice[]) => {
    if (devices.length === 1) {
      const dbg = devices[0];
      device.hydrate({
        macAddress: dbg.macAddress,
        deviceType: dbg.type,
      });
      navigate(`/device-overview/${dbg.uuid}/configuration`);
      return;
    }
    const uuids = devices.map((d) => d.uuid).join(',');
    navigate(`/device-overview/configuration?devices=${uuids}&type=${devices[0].type}`);
  };

  // Config schema is per device type, so bulk config is only offered when the
  // selection is a single type (0 selected → unaffected, per-row icon shows).
  const selectionSingleType = React.useMemo(() => {
    const typeById = new Map(devicesWithType.map((d) => [String(d.id), d.type]));
    const types = new Set([...(selectedKeys ?? [])].map((k) => typeById.get(k)).filter(Boolean));
    return types.size <= 1;
  }, [selectedKeys, devicesWithType]);

  const cfg = configAction(onConfigAction);
  const canConfigureDevices = permissions.has('can_edit_all_devices');
  const configWithGate = {
    ...cfg,
    // API_EXTERNAL devices are registered/managed through the public API and have no
    // configurable schema, so they don't get the settings gear.
    isVisible: (d: DBGWithType) =>
      canConfigureDevices && (cfg.isVisible?.(d) ?? true) && selectionSingleType && d.type !== DeviceType.API_EXTERNAL,
  };

  const openDeviceModal = (dbgs: DBGWithType[]) => setSelectedDevices(dbgs);

  const { actionProps, onActionClick, isActionVisible } = useTableRowActions(
    devicesWithType,
    isArchived
      ? [moveAction(openDeviceModal), deleteAction((dbgs) => setDevicesToArchive(dbgs)), ...decommissionActions]
      : [
          ...(config.deviceTypes?.find((t) => t === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON)
            ? [navigateMapAction(navigate, site?.uuid, isArchived, (dbg: DBGWithType) => dbg.type)]
            : []),
          configWithGate,
          ...(canUpdateFirmware ? [updateFirmwareAction(setFirmwareDevices)] : []),
          editAction(openDeviceModal),
          ...(isEmployee ? [deleteDeviceDataAction<DBGWithType>((dbgs) => setDataDevices(dbgs))] : []),
          deleteAction((dbgs) => setDevicesToArchive(dbgs)),
        ]
  );

  const showLoadMore = mode === 'paginated' && hasNextPage && !error;

  return (
    <>
      <GenericTable
        config={config}
        orderBy={orderBy}
        asc={ascOrder}
        rows={rows ?? []}
        onClickColumn={handleSort}
        actions={actionProps}
        onActionClick={onActionClick}
        isActionVisible={isActionVisible}
        getRowLabel={(i) => devicesWithType[i]?.macAddress}
        getRowKey={(i) => String(devicesWithType[i]?.id ?? i)}
        selectedKeys={selectedKeys}
        onSelectedKeysChange={onSelectedKeysChange}
        onRefresh={refetch}
        isRefreshing={isRefreshing}
        toolbarActions={toolbarActions}
        csvData={sortedData}
        csvAccessors={{ ...config.sortAccessors, ...config.csvAccessors }}
        onPrepareExport={loadAll}
      />
      {showLoadMore && (
        <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
          Load more
        </Button>
      )}
      {selectedDevices && selectedDeviceDetail && (
        <UpdateDeviceModal
          type={selectedDeviceDetail.type}
          devices={selectedDevices}
          onClose={handleCloseDetail}
          onRefresh={refetch}
          showSiteSelect={isArchived}
          siteOnly={isArchived}
          showMoveHint={!isArchived}
        />
      )}
      {devicesToArchive.length > 0 && (
        <DeleteDeviceModal
          devices={devicesToArchive}
          showHardDelete={isEmployee}
          onClose={() => setDevicesToArchive([])}
          onDelete={(hardDelete) => deleteMutation.mutate(hardDelete)}
          isPending={deleteMutation.isPending}
        />
      )}
      {dataDevices.length > 0 && (
        <DeleteDeviceDataModal devices={dataDevices} onClose={() => setDataDevices([])} onRefresh={refetch} />
      )}
      {decommissionModals}
      {firmwareDevices.length > 0 && (
        <UpdateFirmwareModal
          devices={firmwareDevices}
          plannedDeviceIds={plannedDeviceIds}
          onDevicesChanged={refetch}
          onClose={() => setFirmwareDevices([])}
        />
      )}
    </>
  );
}
